/**
 * File utilities
 */
export abstract class FileUtils {
    public static readonly MAX_FILE_SIZE = 100 * 1024 * 1024;

    public static readonly FOLDER_NAME_REGEX = /^(?! )[ \p{L}\p{N}_.!{([\])}@+=;-]+(?<! )$/u;

    // ===============================================================
    // ===============================================================

    /**
     * Start a download in the browser with an URL
     * @param publicURL Public URL to download
     */
    public static async downloadFile(publicURL: string) {
        const link = document.createElement('a');
        link.href = publicURL;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Format file size to the biggest possible unit
     * @param bytes Size in byte(s)
     * @returns Formatted size
     */
    public static formatFileSize(bytes: number): string {
        if (bytes == 0) return '';
        if (!Number.isFinite(bytes) || bytes < 0) {
            throw new Error('size must be a non-negative finite number');
        }

        // Edge case: 0 bytes
        if (bytes === 0) {
            return '0 Bytes';
        }

        // Unit thresholds (powers of 1024)
        const units: {unit: string; divisor: number}[] = [
            {unit: 'TB', divisor: 1024 ** 4},
            {unit: 'GB', divisor: 1024 ** 3},
            {unit: 'MB', divisor: 1024 ** 2},
            {unit: 'KB', divisor: 1024},
            {unit: 'Bytes', divisor: 1}
        ];

        // Find the largest unit that makes the value >= 1
        for (const {unit, divisor} of units) {
            if (bytes >= divisor) {
                const value = bytes / divisor;
                // Use 1 decimal for KB/MB/GB/TB, 0 decimals for Bytes
                const precision = unit === 'Bytes' ? 0 : 2;
                const formatted = value.toFixed(precision).replace(/\.00$/, '');
                return `${formatted} ${unit}`;
            }
        }

        // Should never hit here, but keep TypeScript happy
        return `${bytes} Bytes`;
    }
}
